import { useState } from 'react';
import { useIntl } from 'react-intl';
import FiltroDrawerNew from 'src/componentes/filtro/FiltroDrawerNew';
import PanelCollapse from 'src/componentes/panel-collapse';
import Checkbox from 'src/componentes/checkbox';
import { Checkbox as FormCheckbox } from 'src/componentes/Form';
import InputNumber from 'src/componentes/input-number';
import SearchBar from 'src/componentes/filtro/search-drawer/SearchBar';
import { matchesSearch } from 'src/componentes/filtro/search-drawer/searchDrawerUtils';
import DesenharCampoCustomizado from 'src/paginas/item/campo-customizado/componentes/desenhar';
import PeriodoData from 'src/paginas/data/periodo-data';
import MatrizRiscoLookup from 'src/lookups/matriz-risco';
import ItemOrganizacaoLookup from 'src/lookups/item-organizacao';
import ProcessoLookup from 'src/lookups/processo';
import ColaboradorEGrupoSimplificadoLookup from 'src/lookups/ColaboradorEGrupoSimplificadoLookup';
import StatusRiscoLookup from 'src/lookups/status-item';
import useAuth from 'src/hooks/useAuth';
import { STATUS_ITEM, TIPO_FILTRO_DATA } from 'src/utils/constants';
import {
  criarFiltroDefault,
  filtroSchema,
  limparCamposCustomizados,
  normalizarFiltro,
  obterLabelsFiltro,
} from './filtro.utils';

const CamposFiltroMatrizItensImpactados = ({
  model,
  configuracao,
  periodos,
}) => {
  const intl = useIntl();
  const { terms: resources } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const form = model.value;
  const labels = obterLabelsFiltro(intl, resources);

  const handleChange = (field, value) => {
    model.getModel(field).requestChange(value);
  };

  const getModel = (field) => ({
    ...model.getModel(field),
    label: labels[field],
  });

  const lookup = (field, Component, props = {}) => ({
    field,
    label: labels[field],
    content: <Component multi clearable {...props} model={getModel(field)} />,
  });

  const checkbox = (field) => ({
    field,
    label: labels[field],
    content: <Checkbox model={getModel(field)} />,
  });

  const switchField = (field) => ({
    field,
    label: labels[field],
    content: (
      <div className="tw:flex tw:items-center tw:gap-3">
        <span>{labels[field]}</span>
        <FormCheckbox
          id={`switch-drawer-${field}`}
          type="switch"
          name={field}
          checked={form[field] ?? false}
          onChange={(e) => handleChange(field, e.target.checked)}
        />
      </div>
    ),
  });

  const sections = [
    {
      title: intl.formatMessage({ id: 'label.informacoesBasicas' }),
      fields: [
        lookup('matrizRisco', MatrizRiscoLookup, {
          multi: false,
          clearable: false,
          required: true,
        }),
        {
          field: 'tipoFiltroData',
          label: labels.tipoFiltroData,
          content: (
            <PeriodoData
              clearable
              data={{
                idsIgnorados: [TIPO_FILTRO_DATA.DESDE_QUANDO_HA_VALORES],
              }}
              options={periodos}
              tipoFiltroData={form.tipoFiltroData}
              dataInicio={form.dataInicio}
              dataTermino={form.dataTermino}
              onChange={handleChange}
              isDrawerFilter
            />
          ),
        },
        lookup('areas', ItemOrganizacaoLookup, {
          data: { tiposIdSelecionaveis: [1, 2, 3] },
        }),
        checkbox('areasSubordinadas'),
        lookup('processos', ProcessoLookup, { data: { TipoId: 5 } }),
        checkbox('incluirSubprocessos'),
        switchField('consolidarRiscosSubItensNosItensSuperiores'),
        lookup('responsaveis', ColaboradorEGrupoSimplificadoLookup, {
          abrirEmDrawer: true,
          compactMulti: true,
        }),
        lookup('statusRisco', StatusRiscoLookup, {
          data: {
            idsSelecionados: [
              STATUS_ITEM.EM_PLANEJAMENTO,
              STATUS_ITEM.ATIVO,
              STATUS_ITEM.CONCLUIDO,
              STATUS_ITEM.SUSPENSO,
              STATUS_ITEM.CANCELADO,
              STATUS_ITEM.REPROVADO,
              STATUS_ITEM.REPROVADO_AGUARDANDO_SUBMISSAO,
            ],
          },
        }),
        configuracao?.habilitarCalculoDeScores && {
          field: 'scoreRisco',
          label: labels.scoreRisco,
          content: <InputNumber model={getModel('scoreRisco')} />,
        },
      ],
    },
  ];

  const customFields = (form.camposCustomizados || []).map((field) => ({
    id: field.idCampo,
    label: field.descricaoCampo || field.campo?.nome,
  }));
  const customTitle = intl.formatMessage({ id: 'informacoesComplementares' });
  const visibleCustomFields = matchesSearch(customTitle, searchQuery)
    ? customFields
    : customFields.filter((field) => matchesSearch(field.label, searchQuery));

  return (
    <div>
      <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
      {sections.map((section, index) => {
        const fields = section.fields
          .filter(Boolean)
          .filter(
            (field) =>
              matchesSearch(section.title, searchQuery) ||
              matchesSearch(field.label, searchQuery)
          );
        if (!fields.length) return null;

        return (
          <PanelCollapse
            key={section.title}
            header={section.title}
            open={index === 0 || Boolean(searchQuery)}
            ehAccordionDrawer
          >
            {fields.map((field) => (
              <div key={field.field} className="tw:mb-3">
                {field.content}
              </div>
            ))}
          </PanelCollapse>
        );
      })}
      {visibleCustomFields.length > 0 && (
        <PanelCollapse
          header={customTitle}
          open={Boolean(searchQuery)}
          ehAccordionDrawer
        >
          <DesenharCampoCustomizado
            model={getModel('camposCustomizados')}
            mdFields={12}
            visibleFields={visibleCustomFields}
            useVisibleFields={Boolean(searchQuery)}
            usarFiltroMultiSelecaoListaOpcao
            disabled={false}
          />
        </PanelCollapse>
      )}
    </div>
  );
};

const FiltroDrawerMatrizItensImpactados = ({
  filtro,
  configuracao,
  periodos,
  onSearch,
  onClose,
}) => (
  <FiltroDrawerNew
    tipoLayout="drawer"
    isOpen
    handleClose={onClose}
    modelValue={filtro}
    selectedFilter={{ valor: filtro }}
    search={filtro}
    searchSchema={filtroSchema}
    searchComponent={
      <CamposFiltroMatrizItensImpactados
        configuracao={configuracao}
        periodos={periodos}
      />
    }
    ignorarCamposCustomizados
    usarFiltroSalvo={false}
    onSearch={onSearch}
    getClearedFilter={(current) => ({
      ...normalizarFiltro(criarFiltroDefault(), periodos, current.matrizRisco),
      camposCustomizados: limparCamposCustomizados(current.camposCustomizados),
    })}
  />
);

export default FiltroDrawerMatrizItensImpactados;
